import Helpers from "../../../../../Site/gulp/scripts/typescript/modules/helpers";
import Logger from "../../../../../Site/gulp/scripts/typescript/modules/Logger";

interface HelpFormTopic {
	guid: string;
	questionText: string;
	answerTitle?: string;
	answerText?: string;
	hasAnswerText?: boolean;
	readMoreLinkUrl?: string;
	readMoreLinkText?: string;
	hasReadMoreLinkUrl: boolean;
	formQuestions: FormQuestion[];
};

interface FormQuestion {
	guid: string;
	questionText: string;
	answerTitle: string;
	answerText: string;
	hasAnswerText: boolean;
	readMoreLinkUrl: string;
	readMoreLinkText: string;
	hasReadMoreLinkUrl: boolean;
}

interface HelpFormData extends Array<HelpFormTopic> { }

export default class HelpForm {

	private formOpenButtons: NodeListOf<HTMLButtonElement>;
	private formCloseButtons: NodeListOf<HTMLButtonElement>;
	private formContainer: HTMLElement;
	private formFieldsContainer: HTMLElement;
	private data: HelpFormData;
	private numberOfQuestionLevels: number;
	private selectedFormTopic: string;
	private selectedFormQuestion: string;
	private formTopicInput: HTMLInputElement;
	private formQuestionInput: HTMLInputElement;

	constructor() {

		if (!document.querySelector('.js-help-form-container')) {
			return;
		}

		const dataElement = document.querySelector('.js-help-form-data');
		if (dataElement) {
			try {
				this.data = JSON.parse(dataElement.innerHTML);
			} catch (error) {
				Logger.trackException(error);
				return;
			}
		}

		this.formContainer = document.querySelector('.js-help-form');
		this.formFieldsContainer = document.querySelector('.js-help-form-container');
		this.formOpenButtons = document.querySelectorAll('.js-help-form-open');
		this.formCloseButtons = document.querySelectorAll('.js-help-form-close');
		this.formTopicInput = document.querySelector('.js-selected-form-topic-input') as HTMLInputElement;
		this.formQuestionInput = document.querySelector('.js-selected-form-question-input') as HTMLInputElement;
		this.selectedFormTopic = this.formTopicInput && this.formTopicInput.value ? this.formTopicInput.value : null;
		this.selectedFormQuestion = this.formQuestionInput && this.formQuestionInput.value ? this.formQuestionInput.value : null;
		this.renderFormTopics();
		this.renderFormQuestions();

		this.attachEvents();

	}

	renderFormTopics() {

		if (!document.querySelector('.js-help-form-topics')) {
			return;
		}

		let questionsHTML = `<ul class="c-help-list c-help-list--form" data-test="form-topics-list">`;

		for (var i = 0; i < this.data.length; i++) {
			const currentQuestion = this.data[i];
			questionsHTML += `
				<li class="c-help-list__item" aria-hidden="${this.selectedFormTopic && this.selectedFormTopic !== currentQuestion.guid ? "true" : "false"}">
					<button type="button" class="c-help-list__link c-help-list__link--button js-help-form-topic-button" data-help-id="${currentQuestion.guid}" data-test="form-topic">
						${currentQuestion.questionText}
					</button>
					<button type="button" class="c-help-list__link-change js-help-form-topic-button" data-help-id="${currentQuestion.guid}" data-test="form-topic-change" aria-hidden="${this.selectedFormTopic === currentQuestion.guid ? "false" : "true"}">Change <span class="u-visuallyhidden">answer</span></button>
				</li>
			`;
		}
		questionsHTML += '</ul>';
		document.querySelector('.js-help-form-topics').innerHTML = questionsHTML;

		if (this.selectedFormTopic) {
			const selectedTopicElement = document.querySelector('.js-help-form-topic-button[data-help-id="' + this.selectedFormTopic + '"]') as HTMLButtonElement;
			selectedTopicElement.classList.add('is-selected');
			selectedTopicElement.focus();

			const selectedTopic = this.getSelectedTopic();

			if (!selectedTopic.formQuestions.length) {
				if (selectedTopic && selectedTopic.hasAnswerText) {
					this.renderAnswer(selectedTopic, document.querySelector('.js-help-form-topics'));
				}
				this.numberOfQuestionLevels = 1;
				this.showForm();
			} else {
				this.numberOfQuestionLevels = 2;
				this.hideForm();
			}
		}

	}

	renderFormQuestions() {

		const questionsContainer = document.querySelector('.js-help-form-questions-container');

		if (!questionsContainer) {
			return;
		}

		if (!this.selectedFormTopic || this.numberOfQuestionLevels === 1) {
			questionsContainer.setAttribute('aria-hidden', 'true');
			return;
		}

		questionsContainer.setAttribute('aria-hidden', 'false');

		let questionsHTML = `<ul class="c-help-list c-help-list--form" data-test="form-questions-list">`;

		const data = this.getSelectedTopic();

		for (var i = 0; i < data.formQuestions.length; i++) {
			const currentQuestion = data.formQuestions[i];
			questionsHTML += `
					<li class="c-help-list__item" aria-hidden="${this.selectedFormQuestion && this.selectedFormQuestion !== currentQuestion.guid ? "true" : "false"}">
						<button type="button" class="c-help-list__link c-help-list__link--button js-help-form-question-button" data-help-id="${currentQuestion.guid}" data-test="form-question">
							${currentQuestion.questionText}
						</button>
						<button type="button" class="c-help-list__link-change js-help-form-question-button" data-help-id="${currentQuestion.guid}" data-test="form-question-change" aria-hidden="${this.selectedFormQuestion === currentQuestion.guid ? "false" : "true"}">Change <span class="u-visuallyhidden">answer</span></button>
					</li>
				`;
		}

		questionsHTML += `</ul>`;

		document.querySelector('.js-help-form-questions').innerHTML = questionsHTML;

		if (this.selectedFormQuestion) {
			const selectedQuestionElement = document.querySelector('.js-help-form-question-button[data-help-id="' + this.selectedFormQuestion + '"]') as HTMLButtonElement;
			selectedQuestionElement.classList.add('is-selected');
			selectedQuestionElement.focus();

			const selectedQuestion = data.formQuestions.find((question) => question.guid === this.selectedFormQuestion);
			if (selectedQuestion && selectedQuestion.hasAnswerText) {
				this.renderAnswer(selectedQuestion, document.querySelector('.js-help-form-questions'));
			}

			this.showForm();
		} else {
			this.hideForm();
		}
	}

	private renderAnswer(selectedQuestion: FormQuestion | HelpFormTopic, outputLocation: HTMLElement) {
		const answerHTML = `
					<div class="c-help-form__info js-help-form-info" data-test="answer-block">
						<h3 class="c-help-form__info-title">${selectedQuestion.answerTitle}</h3>
						<div class="c-help-form__info-text c-cms">
							${selectedQuestion.answerText}
						</div>
						${selectedQuestion.hasReadMoreLinkUrl ?
				`<a href= "${selectedQuestion.readMoreLinkUrl}" class="c-help-form__info-link" data-test="answer-block-link" aria-label="Read more about ${selectedQuestion.answerTitle}">${selectedQuestion.readMoreLinkText}</a>`
				: ''}
					</div>
					`;
		outputLocation.insertAdjacentHTML('beforeend', answerHTML);
	}

	private getSelectedTopic() {
		/*@ts-ignore*/
		return this.data.find((item) => item.guid === this.selectedFormTopic);
	}

	private attachEvents() {
		document.querySelector('.js-help-form').addEventListener('click', (e) => {
			const target = e.target as HTMLElement;

			// topic button
			if (target && target.classList.contains('js-help-form-topic-button')) {
				this.hideForm();
				this.selectedFormQuestion = null;
				this.formQuestionInput.value = "";
				if (this.selectedFormTopic === target.getAttribute('data-help-id')) {
					this.selectedFormTopic = null;
					this.formTopicInput.value = "";
					this.renderFormTopics();
					this.renderFormQuestions();
				} else {
					this.selectedFormTopic = target.getAttribute('data-help-id');
					this.renderFormTopics();
					this.renderFormQuestions();
					if (this.numberOfQuestionLevels === 1) {
						this.formQuestionInput.value = target.getAttribute('data-help-id');
						this.formTopicInput.value = target.getAttribute('data-help-id');
					} else {
						this.formTopicInput.value = target.getAttribute('data-help-id');
					}
					this.scrollToForm();

				}
			}

			// question button 
			if (target && target.classList.contains('js-help-form-question-button')) {
				if (this.selectedFormQuestion === target.getAttribute('data-help-id')) {
					this.selectedFormQuestion = null;
					this.formQuestionInput.value = "";
					this.renderFormQuestions();
				} else {
					this.selectedFormQuestion = target.getAttribute('data-help-id');
					this.formQuestionInput.value = target.getAttribute('data-help-id');
					this.renderFormQuestions();
					this.scrollToForm();
				}
			}

		});

		if (this.formOpenButtons.length) {

			Helpers.scopedLoop(this.formOpenButtons, (item, i, context) => {

				item.addEventListener('click', (e) => {

					e.preventDefault();

					this.formContainer.setAttribute('aria-hidden', "false");
					this.scrollToForm();

				});

			}, this);

		}

		if (this.formCloseButtons.length) {

			Helpers.scopedLoop(this.formCloseButtons, (item, i, context) => {

				item.addEventListener('click', (e) => {

					e.preventDefault();

					this.formContainer.setAttribute('aria-hidden', "true");

				});

			}, this);

		}
	}

	private scrollToForm() {
		this.formContainer.scrollIntoView(); 
	}

	private showForm() {
		this.formFieldsContainer.setAttribute('aria-hidden', 'false');
	}

	private hideForm() {
		this.formFieldsContainer.setAttribute('aria-hidden', 'true');
	}

}
